import React from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, Vector3 } from 'three';
import { Sphere, Trail } from '@react-three/drei';
import { useGlobal } from '../../../logic/main';
import { boardConfig } from '../../../config/boardConfig';
import { CustomColors } from '../../../styles/variables';

export interface AnimatedLineProps {
    source: Vector3 | string;
    target: Vector3 | string;
    speed?: number;
    loop?: boolean;
    color?: string;
    width?: number;
    length?: number;
    attenuation?: boolean;
    // Only one trail per speed or multiple with delay
    instances?: number;
    delay?: number;
}

const equals = function(v1: Vector3, v2: Vector3, epsilon = 4) {
    return v1 && v2
        ? Math.abs(v1.x - v2.x) < epsilon && Math.abs(v1.y - v2.y) < epsilon && Math.abs(v1.z - v2.z) < epsilon
        : false;
};

export const AnimatedLines: React.FC = () => {
    const [linesToDraw] = useGlobal(state => state.linesToDraw);
    const lines = React.useRef<Mesh[][]>([]);

    const getPosition = (def: string | Vector3): Vector3 => {
        if (def instanceof Vector3) {
            return def;
        } else {
            try {
                return boardConfig.assets.find(a => a.id === def)!.ref!.position;
            } catch (e) {
                console.error('Could not find position of', def);
                return new Vector3(0, 0, 0);
            }
        }
    };

    useFrame((_, delta) => {
        linesToDraw?.forEach(
            (
                {
                    source,
                    target,
                    speed = 2,
                    loop = true,
                    attenuation = true,
                    color = '#F8D62840',
                    instances = 3,
                    delay = 750,
                    length = 10,
                    width = 0.75
                },
                i
            ) => {
                Array.from(Array(instances).keys()).forEach(instance => {
                    setTimeout(() => {
                        lines.current?.[i]?.[instance]?.position?.lerp(getPosition(target), delta * speed);
                    }, instance * delay);

                    if (loop && equals(lines.current?.[i]?.[instance]?.position, getPosition(target))) {
                        // console.log('Resetting line!');
                        lines.current[i][instance].position.set(
                            getPosition(source).x,
                            getPosition(source).y,
                            getPosition(source).z
                        );
                    }
                });
            }
        );
    });

    return linesToDraw && linesToDraw.length > 0 ? (
        <>
            {linesToDraw?.map(
                (
                    {
                        length = 5,
                        width = 10,
                        color = CustomColors.secondary,
                        source,
                        instances = 3,
                        attenuation = true
                    },
                    i
                ) =>
                    Array.from(Array(instances).keys()).map(instance => (
                        <Trail
                            width={width}
                            length={length}
                            color={color}
                            attenuation={(t: number) => (t * t) / 2}
                            key={`animated-line-${i}-${instance}`}
                        >
                            <Sphere
                                ref={(el: Mesh) => {
                                    if (!lines.current[i]) {
                                        lines.current[i] = [];
                                    }
                                    lines.current[i][Number(instance)] = el;
                                }}
                                args={[0.025, 0.025, 0.025]}
                                position={getPosition(source)}
                            >
                                <meshBasicMaterial color={color?.replace('#', '0x')} transparent={true} opacity={0} />
                            </Sphere>
                        </Trail>
                    ))
            )}
        </>
    ) : null;
};
